import { axiosInstance } from './axiosInstance'

// UBQ-5212: sending is just `inviteUser` (see userServices) — it accepts `emails[]` for a bulk invite
// and `resendUserIds[]` for a bulk resend, and returns the API's own message either way. Only the
// live address check needs its own call, because it runs while someone is still typing.
//
// UBQ-5486: that call now also returns a batchId when the work was handed to the background worker, and
// these read the batch's progress. Deliberately NOT going through userServices' makeRequest wrapper: it
// toasts on every call and swallows the exception, returning undefined. A poll must stay silent and let
// the caller decide — the same style as bulkCompletionService (UBQ-4542).

const BASE = '/users/inviteUser/batches'

/** No STOPPED: once a batch starts it always runs to completion. */
export type BulkInviteStatus = 'RUNNING' | 'COMPLETED'

export interface BulkInviteProgress {
    batchId: string
    /**
     * Must be sent back on every progress call. It bounds the server's ledger read to a single
     * hypertable chunk, so treat it as part of the batch's identity, not an optional extra.
     */
    startedAt: number
    total: number
    sent: number
    failed: number
    pending: number
    status: BulkInviteStatus
    /** Server-driven poll delay; 0 when terminal. */
    pollAfterMs: number
}

/** Progress for one batch. */
export const getBulkInvitationProgress = async (
    batchId: string,
    orgId: string,
    startedAt: number,
): Promise<BulkInviteProgress> => {
    const { data } = await axiosInstance.get<BulkInviteProgress>(
        `${BASE}/${batchId}`,
        { params: { orgId, startedAt } },
    )
    return data
}

/** Batches still sending for this account, so progress survives a page reload. */
export const getActiveBulkInvitations = async (
    orgId: string,
): Promise<BulkInviteProgress[]> => {
    const { data } = await axiosInstance.get<BulkInviteProgress[]>(BASE, {
        params: { orgId },
    })
    return data ?? []
}

/**
 * Which of these addresses can be invited to this account.
 * Verdicts: 'ok' | 'already_active' | 'already_invited' | 'deactivated', keyed by lower-cased address.
 */
export const checkInviteEmails = async (
    orgId: string,
    emails: string[],
): Promise<Record<string, string>> => {
    if (emails.length === 0) return {}
    const { data } = await axiosInstance.post<Record<string, string>>(
        '/users/check-emails',
        emails,
        { params: { orgId } },
    )
    return data ?? {}
}
